import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { FileText, Send, CheckCircle, Clock, Edit, Plus, Download, Mail } from 'lucide-react';

const ContractManager = ({ selectedProperty, properties, onContractCreated }) => {
  const [contracts, setContracts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [showCreateContract, setShowCreateContract] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [newContract, setNewContract] = useState({
    tenant_name: '',
    tenant_email: '',
    tenant_phone: '',
    monthly_rent: '',
    security_deposit: '',
    lease_start: '',
    lease_end: '',
    contract_type: 'whole_house',
    room_id: null,
    template_id: null
  });
  const [newTemplate, setNewTemplate] = useState({
    template_name: '',
    template_type: 'whole_house',
    template_content: ''
  });

  useEffect(() => {
    if (selectedProperty) {
      fetchContracts();
      fetchTemplates();
    }
  }, [selectedProperty]);

  const fetchContracts = async () => {
    try {
      const response = await axios.get(`/api/properties/${selectedProperty.id}/contracts`);
      setContracts(response.data.data);
    } catch (error) {
      console.error('Error fetching contracts:', error);
      toast.error('Failed to fetch contracts');
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get('/api/properties/contract-templates');
      setTemplates(response.data.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Failed to fetch templates');
    }
  };

  const handleCreateContract = async (e) => {
    e.preventDefault();
    try {
      // Process template if one is selected
      let contractData = { ...newContract };
      
      if (newContract.template_id) {
        const selectedTemplate = templates.find(t => t.id === parseInt(newContract.template_id));
        if (selectedTemplate) {
          // Process the template content with contract data
          const processedContent = processTemplate(selectedTemplate.template_content, {
            ...contractData,
            property_name: selectedProperty?.name || '',
            property_address: selectedProperty?.address || ''
          });
          
          // Add processed content to contract data
          contractData.processed_content = processedContent;
          contractData.template_used = selectedTemplate.template_name;
        }
      }
      
      const response = await axios.post(`/api/properties/${selectedProperty.id}/contracts`, contractData);
      
      if (response.data.success) {
        toast.success('Contract created successfully!');
        setShowCreateContract(false);
        fetchContracts(); // Refresh contracts list
        if (onContractCreated) {
          onContractCreated();
        }
        setNewContract({
          tenant_name: '',
          tenant_email: '',
          tenant_phone: '',
          monthly_rent: '',
          security_deposit: '',
          lease_start: '',
          lease_end: '',
          contract_type: 'whole_house',
          room_id: null,
          template_id: null
        });
        fetchContracts();
      }
    } catch (error) {
      console.error('Error creating contract:', error);
      toast.error('Failed to create contract');
    }
  };

  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post('/api/properties/contract-templates', newTemplate);
      
      if (response.data.success) {
        toast.success('Template created successfully!');
        setShowTemplates(false);
        setNewTemplate({
          template_name: '',
          template_type: 'whole_house',
          template_content: ''
        });
        fetchTemplates();
      }
    } catch (error) {
      console.error('Error creating template:', error);
      toast.error('Failed to create template');
    }
  };

  const handleSendForSignature = async (contractId, tenantEmail, tenantName) => {
    try {
      const response = await axios.post(`/api/properties/contracts/${contractId}/send-for-signature`, {
        tenantEmail,
        tenantName
      });
      
      if (response.data.success) {
        toast.success('Contract sent for signature!');
        // In a real implementation, this would send an email
        console.log('Signature URL:', response.data.data.signatureUrl);
        console.log('Verification Code:', response.data.data.verificationCode);
      }
    } catch (error) {
      console.error('Error sending for signature:', error);
      toast.error('Failed to send contract for signature');
    }
  };

  const getContractStatus = (contract) => {
    if (contract.esign_status === 'signed') return { status: 'Signed', color: 'green', icon: CheckCircle };
    if (contract.esign_status === 'pending') return { status: 'Pending Signature', color: 'yellow', icon: Clock };
    return { status: 'Draft', color: 'gray', icon: Edit };
  };

  const processTemplate = (template, contractData) => {
    if (!template || !contractData) return '';
    
    let processedTemplate = template;
    
    // Replace placeholders with actual values
    processedTemplate = processedTemplate.replace(/\[TENANT_NAME\]/g, contractData.tenant_name || '');
    processedTemplate = processedTemplate.replace(/\[TENANT_EMAIL\]/g, contractData.tenant_email || '');
    processedTemplate = processedTemplate.replace(/\[TENANT_PHONE\]/g, contractData.tenant_phone || '');
    processedTemplate = processedTemplate.replace(/\[MONTHLY_RENT\]/g, contractData.monthly_rent || '');
    processedTemplate = processedTemplate.replace(/\[SECURITY_DEPOSIT\]/g, contractData.security_deposit || '');
    processedTemplate = processedTemplate.replace(/\[LEASE_START\]/g, contractData.lease_start || '');
    processedTemplate = processedTemplate.replace(/\[LEASE_END\]/g, contractData.lease_end || '');
    processedTemplate = processedTemplate.replace(/\[LANDLORD_NAME\]/g, 'Property Owner');
    processedTemplate = processedTemplate.replace(/\[PROPERTY_ADDRESS\]/g, selectedProperty?.address || '');
    processedTemplate = processedTemplate.replace(/\[PROPERTY_NAME\]/g, selectedProperty?.name || '');
    processedTemplate = processedTemplate.replace(/\[ROOM_NAME\]/g, contractData.room_name || '');
    processedTemplate = processedTemplate.replace(/\[DATE\]/g, new Date().toLocaleDateString());
    
    return processedTemplate;
  };

  const defaultTemplates = {
    whole_house: `LEASE AGREEMENT

Property: [PROPERTY_NAME]
Address: [PROPERTY_ADDRESS]

TENANT INFORMATION:
Name: [TENANT_NAME]
Email: [TENANT_EMAIL]
Phone: [TENANT_PHONE]

LEASE TERMS:
Monthly Rent: $[MONTHLY_RENT]
Security Deposit: $[SECURITY_DEPOSIT]
Lease Start: [LEASE_START]
Lease End: [LEASE_END]

TERMS AND CONDITIONS:
1. Rent is due on the 1st of each month
2. Late fees apply after 5 days
3. No pets without written permission
4. Tenant responsible for utilities
5. 30-day notice required for termination

LANDLORD: [LANDLORD_NAME]
TENANT: [TENANT_NAME]

Date: [DATE]`,

    individual_room: `ROOM RENTAL AGREEMENT

Property: [PROPERTY_NAME]
Room: [ROOM_NAME]
Address: [PROPERTY_ADDRESS]

TENANT INFORMATION:
Name: [TENANT_NAME]
Email: [TENANT_EMAIL]
Phone: [TENANT_PHONE]

RENTAL TERMS:
Monthly Rent: $[MONTHLY_RENT]
Security Deposit: $[SECURITY_DEPOSIT]
Lease Start: [LEASE_START]
Lease End: [LEASE_END]

SHARED SPACES:
- Kitchen: Shared with other tenants
- Living Room: Shared with other tenants
- Bathroom: Shared with other tenants

TERMS AND CONDITIONS:
1. Rent is due on the 1st of each month
2. Respect shared spaces and other tenants
3. No overnight guests without permission
4. Keep room clean and tidy
5. 30-day notice required for termination

LANDLORD: {{LANDLORD_NAME}}
TENANT: {{TENANT_NAME}}

Date: {{DATE}}`
  };

  if (!selectedProperty) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-center py-12">
          <FileText className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No Property Selected</h3>
          <p className="mt-1 text-sm text-gray-500">Please select a property to manage contracts.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Contract Management - {selectedProperty.name}</h2>

        </div>

        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{selectedProperty.name}</h3>
          <p className="text-gray-600">{selectedProperty.address}</p>
        </div>

        {/* Action Buttons */}
        <div className="mb-6 flex space-x-4">
          <button
            onClick={() => setShowCreateContract(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Contract
          </button>
          <button
            onClick={() => setShowTemplates(true)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <FileText className="w-4 h-4 mr-2" />
            Manage Templates
          </button>
        </div>

        {/* Contracts List */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Existing Contracts</h3>
          {contracts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {contracts.map(contract => {
                const statusInfo = getContractStatus(contract);
                const StatusIcon = statusInfo.icon;
                
                return (
                  <div key={contract.id} className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-semibold text-gray-900">{contract.tenant_name}</h4>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${statusInfo.color}-100 text-${statusInfo.color}-800`}>
                        <StatusIcon className="w-3 h-3 mr-1" />
                        {statusInfo.status}
                      </span>
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-1">{contract.tenant_email}</p>
                    <p className="text-sm text-gray-600 mb-1">${contract.monthly_rent}/month</p>
                    <p className="text-sm text-gray-500 mb-3">
                      {contract.lease_start} to {contract.lease_end}
                    </p>
                    
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleSendForSignature(contract.id, contract.tenant_email, contract.tenant_name)}
                        className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700"
                      >
                        <Send className="w-3 h-3 mr-1" />
                        Send for Signature
                      </button>
                      <button className="inline-flex items-center px-3 py-1 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50">
                        <Download className="w-3 h-3 mr-1" />
                        Download
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <FileText className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No contracts yet</h3>
              <p className="mt-1 text-sm text-gray-500">Create your first contract to get started.</p>
            </div>
          )}
        </div>

        {/* Create Contract Modal */}
        {showCreateContract && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-60">
            <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Create New Contract</h3>
              <form onSubmit={handleCreateContract}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Contract Type</label>
                    <select
                      value={newContract.contract_type}
                      onChange={(e) => setNewContract({...newContract, contract_type: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="whole_house">Whole House</option>
                      <option value="individual_room">Individual Room</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Template</label>
                    <select
                      value={newContract.template_id || ''}
                      onChange={(e) => setNewContract({...newContract, template_id: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select template...</option>
                      {templates.map(template => (
                        <option key={template.id} value={template.id}>
                          {template.template_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Tenant Name</label>
                    <input
                      type="text"
                      value={newContract.tenant_name}
                      onChange={(e) => setNewContract({...newContract, tenant_name: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Tenant Email</label>
                    <input
                      type="email"
                      value={newContract.tenant_email}
                      onChange={(e) => setNewContract({...newContract, tenant_email: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Monthly Rent</label>
                    <input
                      type="number"
                      value={newContract.monthly_rent}
                      onChange={(e) => setNewContract({...newContract, monthly_rent: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Security Deposit</label>
                    <input
                      type="number"
                      value={newContract.security_deposit}
                      onChange={(e) => setNewContract({...newContract, security_deposit: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Lease Start</label>
                    <input
                      type="date"
                      value={newContract.lease_start}
                      onChange={(e) => setNewContract({...newContract, lease_start: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Lease End</label>
                    <input
                      type="date"
                      value={newContract.lease_end}
                      onChange={(e) => setNewContract({...newContract, lease_end: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowCreateContract(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                  >
                    Create Contract
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Templates Modal */}
        {showTemplates && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-60">
            <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Contract Templates</h3>
              
              <div className="mb-6">
                <button
                  onClick={() => setSelectedTemplate('new')}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create New Template
                </button>
              </div>

              {selectedTemplate === 'new' && (
                <form onSubmit={handleCreateTemplate} className="mb-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Template Name</label>
                      <input
                        type="text"
                        value={newTemplate.template_name}
                        onChange={(e) => setNewTemplate({...newTemplate, template_name: e.target.value})}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Template Type</label>
                      <select
                        value={newTemplate.template_type}
                        onChange={(e) => setNewTemplate({...newTemplate, template_type: e.target.value})}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="whole_house">Whole House</option>
                        <option value="individual_room">Individual Room</option>
                        <option value="month_to_month">Month to Month</option>
                        <option value="fixed_term">Fixed Term</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700">Template Content</label>
                    <textarea
                      value={newTemplate.template_content}
                      onChange={(e) => setNewTemplate({...newTemplate, template_content: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      rows="15"
                      placeholder="Enter your contract template here. Use variables like [TENANT_NAME], [MONTHLY_RENT], etc."
                    />
                  </div>

                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => setSelectedTemplate(null)}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                    >
                      Save Template
                    </button>
                  </div>
                </form>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {templates.map(template => (
                  <div key={template.id} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">{template.template_name}</h4>
                    <p className="text-sm text-gray-600 mb-2 capitalize">{template.template_type.replace('_', ' ')}</p>
                    <div className="text-sm text-gray-500 mb-3 max-h-32 overflow-y-auto">
                      <pre className="whitespace-pre-wrap text-xs">{template.template_content.substring(0, 500)}{template.template_content.length > 500 ? '...' : ''}</pre>
                    </div>
                    <div className="flex space-x-2">
                      <button className="text-blue-600 hover:text-blue-800 text-sm">Edit</button>
                      <button className="text-red-600 hover:text-red-800 text-sm">Delete</button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowTemplates(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default ContractManager;
